import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Globe2, Sparkles, Building2, MapPin, Phone, Mail, Ruler, DollarSign, Images, ShieldCheck, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ExpoStand Exhibitor Landing Page
 * - Production-ready single-file React component
 * - Tailwind CSS utility classes
 * - Minimal dependencies: framer-motion, lucide-react (icons)
 *
 * How to use
 * 1) Drop this file into your React project (e.g., src/ExhibitorLanding.jsx) and set it as the route for /exhibitor/landing
 * 2) Ensure Tailwind is configured. No custom components required.
 * 3) Replace placeholder images/links with your assets.
 */

const cities = [
  { name: "İstanbul", country: "Türkiye", shows: "WIN Eurasia, Automechanika, Foodex", slug: "istanbul", img: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?q=80&w=800&auto=format&fit=crop" },
  { name: "Dubai", country: "BAE", shows: "GITEX, Arab Health, Gulfood", slug: "dubai", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=800&auto=format&fit=crop" },
  { name: "Frankfurt", country: "Almanya", shows: "Automechanika, Book Fair, Ambiente", slug: "frankfurt", img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=800&auto=format&fit=crop" },
  { name: "Hannover", country: "Almanya", shows: "Hannover Messe, Agritechnica, EMO", slug: "hannover", img: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?q=80&w=800&auto=format&fit=crop" },
  { name: "Barcelona", country: "İspanya", shows: "Mobile World Congress, Alimentaria", slug: "barcelona", img: "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?q=80&w=800&auto=format&fit=crop" },
  { name: "Milan", country: "İtalya", shows: "Salone del Mobile, EICMA, Host", slug: "milan", img: "https://images.unsplash.com/photo-1520175480921-4edfa2983e0f?q=80&w=800&auto=format&fit=crop" },
];

const gallery = [
  { title: "10×10 Inline — Las Vegas", img: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1600&auto=format&fit=crop" },
  { title: "6×7m Island — Dubai", img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1600&auto=format&fit=crop" },
  { title: "Custom Lightbox — Munich", img: "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?q=80&w=1600&auto=format&fit=crop" },
  { title: "Product Tower — Chicago", img: "https://images.unsplash.com/photo-1529336953121-adb2defc4f79?q=80&w=1600&auto=format&fit=crop" },
  { title: "Immersive AV — Orlando", img: "https://images.unsplash.com/photo-1484704849700-f032a568e944?q=80&w=1600&auto=format&fit=crop" },
  { title: "Premium Lounge — Shanghai", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1600&auto=format&fit=crop" },
];

export default function ExhibitorLanding() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    showCity: "",
    booth: "",
    budget: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      setSubmitError("Please fill in your name and email.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${API}/public/inquiry`, {
        inquiry_type: "brief",
        target_type: "contractor",
        target_slug: "expostand-portal",
        name: form.name,
        email: form.email,
        company: form.company,
        show_name: form.showCity,
        stand_sqm: form.booth,
        budget: form.budget,
        notes: form.message || "Brief inquiry from Exhibitor Landing",
      });
      setSubmitted(true);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER */}
      <header className="bg-white px-4 md:px-6 py-4 relative z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
              alt="EXPO STAND Logo" 
              className="w-10 h-8 md:w-12 md:h-10" 
            />
            <div className="text-slate-700">
              <div className="text-lg md:text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-lg md:text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>
          
          {/* Navigation - hidden on mobile */}
          <div className="hidden md:flex items-center space-x-6 text-slate-700 text-sm">
            {/* Şehirler link */}
            <Link to="/locations" className="hover:text-blue-600 transition-colors font-medium flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />Şehirler
            </Link>

            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            
            {/* Sign In Dropdown */}
            <div className="relative group">
              <span className="hover:text-blue-600 cursor-pointer flex items-center">
                Sign In <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Exhibitor</a>
                <a href="/contractor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Contractor</a>
                <a href="/supplier/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Supplier</a>
                <div className="border-t border-gray-100 my-1" />
                <a href="/admin-login" className="block px-4 py-2 text-sm text-red-600 font-medium hover:bg-red-50">Admin Login</a>
              </div>
            </div>

            {/* Sign Up Dropdown */}
            <div className="relative group">
              <span className="hover:text-blue-600 cursor-pointer flex items-center">
                Sign Up <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Exhibitor</a>
                <a href="/contractor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Contractor</a>
                <a href="/supplier/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Supplier</a>
              </div>
            </div>
            
            <span className="hover:text-blue-600 cursor-pointer">Contact</span>
          </div>

          {/* Mobile Sign In */}
          <a href="/exhibitor/login" className="md:hidden px-3 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
            Sign In
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden flex items-center" style={{ height: 'calc(100vh - 80px)' }}>
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2400&auto=format&fit=crop"
            alt="Expo hall background"
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/70 to-white" />
        </div>

        <div className="mx-auto max-w-7xl px-6 w-full pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm shadow-sm mb-2">
                <Sparkles className="h-4 w-4" />
                <span>Vetted partners in 46 countries (and growing)</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl leading-tight -mt-2">
                Welcome to the<br />
                <span className="text-blue-600">ExpoStand</span><br />
                Exhibitor Portal
              </h1>
              <p className="mt-4 text-lg leading-7 text-slate-600">
                Your fast lane to trusted booth contractors and event suppliers worldwide. Post a brief in minutes and get matched with quality-driven partners—no guesswork, no spam.
              </p>

              <div className="mt-6 space-y-2">
                <p className="text-sm text-slate-700">
                  <strong>🚀 Get Multiple Quotes</strong> - Receive competitive proposals within 24 hours
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🏆 Quality Assurance</strong> - All partners undergo strict verification
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🌍 Global Coverage</strong> - Major exhibition cities worldwide
                </p>
                <p className="text-sm text-slate-700">
                  <strong>🔐 Secure Process</strong> - Optional escrow protection and standardized contracts
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate("/exhibitor/signup")}
                  data-testid="join-exhibitor-club-btn"
                  className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-white shadow hover:bg-blue-700 transition-colors font-semibold"
                >
                  Join Exhibitor Club <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate("/exhibitor/brief/new")}
                  data-testid="create-brief-btn"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-slate-900 shadow-sm hover:bg-slate-50 transition-colors font-medium"
                >
                  Create Brief <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <ul className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Transparent quotes</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Secure payments</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600"/> Standardized contracts</li>
              </ul>
            </motion.div>

            {/* Right side image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1505236858219-8359eb29e329?q=80&w=600&auto=format&fit=crop"
                  alt="Professional trade fair exhibition stand"
                  className="w-full h-[300px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-800">
                      Professional exhibition stands delivered worldwide
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating stats cards */}
              <div className="absolute -top-2 -right-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">24h</div>
                  <div className="text-xs text-slate-500">Response</div>
                </div>
              </div>
              
              <div className="absolute top-1/2 -left-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">500+</div>
                  <div className="text-xs text-slate-500">Partners</div>
                </div>
              </div>
              
              <div className="absolute -bottom-2 left-1/4 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">46</div>
                  <div className="text-xs text-slate-500">Countries</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-blue-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Save Time & Money—Here's How</h2>
            <p className="mt-2 text-slate-600 mb-4">Three simple steps from brief to booked vendor.</p>
            <p className="text-slate-700">
              Skip the endless phone calls, scattered quotes, and unclear timelines. Our standardized platform connects you with pre-vetted contractors, 
              delivers comparable proposals in 24-48 hours, and protects your payments through optional escrow services. 
              <span className="font-medium text-blue-600"> Average time saved: 15+ hours per project | Cost reduction: 12-18% through competitive bidding.</span>
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Post a Brief",
                desc: "Tell us your booth specs, venue, and dates. We'll route it to the right pros—fast. Fill out our standardized brief form and get matched with verified contractors in your target city within minutes.",
                Icon: Building2,
                badge: "Step 1",
              },
              {
                title: "Compare & Shortlist",
                desc: "Review quotes, portfolios, and reviews side‑by‑side. Message vendors in one thread. Receive detailed proposals in uniform format and chat with multiple vendors simultaneously through our messaging system.",
                Icon: ShieldCheck,
                badge: "Step 2",
              },
              {
                title: "Book with Confidence",
                desc: "E‑sign contracts and optionally use escrow to protect milestones and deliverables. Select your preferred contractor, sign standardized contracts digitally, and release payments only when work is approved.",
                Icon: CheckCircle2,
                badge: "Step 3",
              },
            ].map(({ title, desc, Icon, badge }) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-blue-50" />
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">{badge}</div>
                <Icon className="mb-4 h-8 w-8" />
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mt-2 text-slate-600">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Where We Operate</h2>
              <p className="mt-2 text-slate-600">Active in <span className="font-semibold text-slate-900">46 countries</span>—expanding every week.</p>
            </div>
            <Link to="/locations" className="inline-flex items-center gap-2 text-blue-700 hover:underline font-medium">
              Tüm şehirleri gör ({'>'}11) <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {cities.map((c) => (
              <Link key={c.name} to={`/city/${c.slug}`}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5">
                <div className="relative h-40 w-full overflow-hidden">
                  <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold group-hover:text-blue-700 transition-colors">{c.name}</h3>
                      <p className="text-xs text-slate-400 font-medium">{c.country}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all mt-1" />
                  </div>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-1">{c.shows}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="mb-10 max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight">Plans & Pricing</h2>
          <p className="mt-2 text-slate-600">Choose the plan that fits your trade show program.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[{
            name: "Starter",
            price: "$0",
            period: "/month",
            features: ["Post up to 2 briefs/month", "Access to verified suppliers", "Basic messaging & file sharing"],
            cta: "Get Started",
          },{
            name: "Professional",
            price: "$149",
            period: "/month",
            features: ["Unlimited briefs & shortlists", "Advanced comparison & RFQ tools", "Contract templates + milestone tracking", "Priority support"],
            cta: "Start Free Trial",
            highlighted: true,
          },{
            name: "Enterprise",
            price: "Custom",
            period: "",
            features: ["Multi-brand workspaces & roles", "Custom SLAs and compliance", "API/SSO & procurement integrations", "Dedicated success manager"],
            cta: "Talk to Sales",
          }].map((p) => (
            <div key={p.name} className={`flex h-full flex-col rounded-2xl border bg-white p-6 shadow-sm ${p.highlighted ? "border-blue-600 ring-2 ring-blue-100" : "border-slate-200"}`}>
              <div className="mb-4 flex items-baseline justify-between">
                <h3 className="text-xl font-semibold">{p.name}</h3>
                {p.highlighted && <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">Popular</span>}
              </div>
              <div className="mb-4">
                <span className="text-4xl font-bold">{p.price}</span>
                <span className="text-slate-600">{p.period}</span>
              </div>
              <ul className="mb-6 space-y-2 text-slate-700">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600"/> <span>{f}</span></li>
                ))}
              </ul>
              <a href={p.name === "Enterprise" ? "#/contact" : "#/signup"} className={`mt-auto inline-flex items-center justify-center rounded-2xl px-4 py-2 font-medium shadow-sm ${p.highlighted ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}>{p.cta}</a>
              <p className="mt-3 text-xs text-slate-500">Payment processing/escrow fees apply only when used.</p>
            </div>
          ))}
        </div>
      </section>

      {/* CALLBACK FORM */}
      <section className="bg-slate-50" ref={formRef}>
        <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Create a Brief — We'll Match You with the Best Partners</h2>
            <p className="mt-2 text-slate-600">Tell us about your exhibition needs and we'll connect you with pre-vetted contractors within 24 hours.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {submitted ? (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-10 flex flex-col items-center justify-center text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold text-green-800">Brief Received!</h3>
                <p className="mt-2 text-green-700">We'll get back to you within the same business day with matched contractor proposals.</p>
              </div>
            ) : (
            <form className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Full Name *" placeholder="e.g., Jordan Lee" value={form.name} onChange={(v) => update("name", v)} Icon={Building2} />
                <Field label="Work Email *" placeholder="name@company.com" type="email" value={form.email} onChange={(v) => update("email", v)} Icon={Mail} />
                <Field label="Company" placeholder="Your brand or exhibitor name" value={form.company} onChange={(v) => update("company", v)} Icon={Building2} />
                <Field label="Show & City" placeholder="e.g., Gulfood, Dubai" value={form.showCity} onChange={(v) => update("showCity", v)} Icon={MapPin} />
                <Field label="Booth Size" placeholder="e.g., 6m x 7m" value={form.booth} onChange={(v) => update("booth", v)} Icon={Ruler} />
                <Field label="Target Budget (Optional)" placeholder="e.g., $15,000–$25,000" value={form.budget} onChange={(v) => update("budget", v)} Icon={DollarSign} />
              </div>
              <div className="mt-4">
                <Label>Message</Label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm outline-none ring-offset-background placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600"
                  rows={4}
                  placeholder="Describe your exhibition stand requirements..."
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                />
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
                Okay to contact me about this inquiry.
              </label>
              {submitError && (
                <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitError}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-brief-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-medium text-white shadow hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <><Phone className="h-4 w-4" /> Submit Brief & Get Quotes</>}
              </button>
            </form>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">What to include in your brief</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
                <li>Event name, venue, and dates</li>
                <li>Booth footprint & height limits</li>
                <li>Scope (design/build/graphics/AV/furniture/logistics)</li>
                <li>Brand guidelines & must‑have materials</li>
                <li>On‑site services (install/dismantle, electrical, rigging)</li>
                <li>Budget range & decision timeline</li>
              </ul>
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-5 w-5"/> Optional escrow protects your milestones—funds release only when you approve deliverables.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSPIRING DESIGNS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Inspiring Exhibition Stand Designs</h2>
              <p className="mt-2 text-slate-600">Creative concepts and innovative booth designs that make lasting impressions.</p>
            </div>
            <a href="/design-gallery" className="inline-flex items-center gap-2 text-blue-700 hover:underline">
              See more designs <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {[
              { title: "Modern Interactive Stand", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/1p6p52h6_behance_download_1512683025775.jpg" },
              { title: "Elegant Corporate Display", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/og4pi04c_d99fbd54082151.594c269fa2921.jpg" },
              { title: "Creative Brand Experience", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/wly6v2kk_behance_download_1512846870389.jpg" },
              { title: "Professional Presentation Zone", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/elbz7932_Screen%20Shot%202018-10-28%20at%2007.16.31.png" },
              { title: "Innovative Product Showcase", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/1p6p52h6_behance_download_1512683025775.jpg" },
              { title: "Premium Brand Activation", img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/og4pi04c_d99fbd54082151.594c269fa2921.jpg" },
            ].map((design) => (
              <div key={design.title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="relative h-44 w-full overflow-hidden">
                  <img src={design.img} alt={design.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-slate-900">{design.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Trusted Work From Our Partner Network</h2>
            <p className="mt-2 text-slate-600">Real builds delivered on time and on budget—worldwide.</p>
          </div>
          <a href="/case-studies" className="inline-flex items-center gap-2 text-blue-700 hover:underline">View case studies <ArrowRight className="h-4 w-4"/></a>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
          {gallery.map((g) => (
            <div key={g.title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="relative h-44 w-full overflow-hidden">
                <img src={g.img} alt={g.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <p className="text-sm font-medium text-slate-900">{g.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="grid gap-8 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 text-xl font-bold"><Images className="h-6 w-6"/> ExpoStand</div>
              <p className="mt-2 max-w-md text-sm text-slate-600">Connecting exhibitors with trusted contractors and suppliers, worldwide.</p>
              <p className="mt-4 text-xs text-slate-500">© 2025 ExpoStand. All rights reserved.</p>
            </div>
            <nav className="grid grid-cols-2 gap-8 md:col-span-3 md:grid-cols-3">
              <div>
                <h4 className="text-sm font-semibold">Platform</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><Link to="/locations" className="hover:text-blue-600 hover:underline flex items-center gap-1"><MapPin className="h-3 w-3" />Fuar Şehirleri</Link></li>
                  <li><a href="/exhibitor/landing" className="hover:underline">Katılımcılar için</a></li>
                  <li><a href="/contractor/landing" className="hover:underline">Yükleniciler için</a></li>
                  <li><a href="/supplier/landing" className="hover:underline">Tedarikçiler için</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold">Şehirler</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><Link to="/city/istanbul" className="hover:underline">İstanbul</Link></li>
                  <li><Link to="/city/dubai" className="hover:underline">Dubai</Link></li>
                  <li><Link to="/city/frankfurt" className="hover:underline">Frankfurt</Link></li>
                  <li><Link to="/city/hannover" className="hover:underline">Hannover</Link></li>
                  <li><Link to="/locations" className="hover:text-blue-600 font-medium text-blue-600">Tüm şehirler →</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold">Destek</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/how-it-works" className="hover:underline">Nasıl Çalışır?</a></li>
                  <li><a href="/support" className="hover:underline">Yardım Merkezi</a></li>
                  <li><a href="/legal/terms" className="hover:underline">Kullanım Koşulları</a></li>
                  <li><a href="/legal/privacy" className="hover:underline">Gizlilik</a></li>
                </ul>
              </div>
            </nav>
          </div>
          <p className="mt-6 text-xs text-slate-500">ExpoStand is a marketplace that vets suppliers for quality and reliability. All projects are subject to venue rules and local regulations. Secure payment services are provided by licensed third‑party providers where available.</p>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text", Icon }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-600">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <input
          type={type}
          className="w-full bg-transparent outline-none placeholder:text-slate-400"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="text-sm font-medium text-slate-700">{children}</label>;
}